import * as THREE from 'three';
import { ForceHelper } from "./Viewer.ForceHelper.js";
import { Object3D } from "./Viewer.Object3D.js";

import * as turf from '@turf/turf';

// const _axis = /*@__PURE__*/ new THREE.Vector3();

class DistributedLoadHelper extends Object3D {
    constructor( components = [0, 0, 1, 0, 0, 1],
                 xLoc = [0, 1],
                 labelTexts,
                 isLocalCS = true,
                 isProjection = false,
                 member,
                 lineMaterial,
                 meshMaterial,
                 forceMeshMaterial,
                 color
    ) {

        super();

        this.type = "DistributedLoadHelper";

        this.xLoc = xLoc;
        this.member = member;

        this.isProjection = isProjection;
        this.isLocalCS = isLocalCS;

        this.force1Components = components.slice(0, 3);
        this.force2Components = components.slice(3, 6);

        this.force1Direction = this.force1Components.findIndex(component => component !== 0);
        this.force2Direction = this.force2Components.findIndex(component => component !== 0);

        if (~this.force1Direction) {
            // force1
            this.dirIndex = this.force1Direction;

            this.force1 = new ForceHelper(
                this.force1Components,
                labelTexts[0],
                lineMaterial,
                forceMeshMaterial,
                color
            );

            if (isLocalCS) {
                member.add( this.force1 );
            } else {
                member.attach( this.force1 );
            }

            this.force1.position.set(this.xLoc[0], 0, 0);

        }

        if (~this.force2Direction) {
            // force2
            this.dirIndex = this.force2Direction;

            this.force2 = new ForceHelper(
                this.force2Components,
                labelTexts[1],
                lineMaterial,
                forceMeshMaterial,
                color
            );

            if (isLocalCS) {
                member.add( this.force2 );
            } else {
                member.attach( this.force2 );
            }
            this.force2.position.set(this.xLoc[1], 0, 0);
        }

        const geometry = new THREE.BufferGeometry();

        const vertices = new Float32Array( [
            0, 0, 0,
            0, 1, 1,
            1, 1, 1,
            1, 0, 0
        ] );

        geometry.setAttribute('position', new THREE.BufferAttribute( vertices, 3 ));

        this.meshMaterial = meshMaterial;
        this.lineMaterial = lineMaterial;
        this.forceMeshMaterial = forceMeshMaterial;

        this.objects = [new THREE.Mesh( geometry, this.meshMaterial )];

        this.objects.forEach(obj => {
            this.add(obj);
        });

        this.setScale( 1 );

    }

    // TODO: I'm so sorry, there have to be better way, but I'm too dumb to solve it,
    //  so I will keep it this way till it'll broke
    setScale( scaleFactor ) {

        let indices;
        let positionProjection;  // this variable will hold the position
        // when the load acts in projection and one of the forces is zero

        this.objects.forEach(polygon => {
            this.remove( polygon );
            if (polygon.geometry) polygon.geometry.dispose();
        });

        this.objects = [];

        const shapeCoords = [];

        [this.force1, this.force2].forEach((force) => {
            if (force) {
                force.setScale(scaleFactor);
                force.line.updateMatrixWorld();
            }
        });

        if (this.dirIndex === 0) {
            indices = [0, 2];
        } else {
            indices = [0, this.dirIndex];
        }

        // SET THE FORCES POSITION
        if (this.isProjection) {
            const sign = this.force1Components[this.dirIndex] === 0 ? Math.sign(this.force2Components[this.dirIndex]) : Math.sign(this.force1Components[this.dirIndex]);
            const signStr = sign > 0 ? "+" : sign < 0 ? "-" : "0";

            const dir = `${signStr}${["X", "Y", "Z"][this.dirIndex]}`;

            let coordinate, p1, p2;

            if (this.force1) {
                p1 = new THREE.Vector3( );
                this.force1.getWorldPosition(p1);
            } else {
                p1 = this.member.localToWorld( new THREE.Vector3( this.xLoc[0], 0, 0 ) );
            }

            if (this.force2) {
                p2 = new THREE.Vector3( );
                this.force2.getWorldPosition(p2);
            } else {
                p2 = this.member.localToWorld( new THREE.Vector3( this.xLoc[1], 0, 0 ) );
            }

            switch(dir) {
                case "+X":
                    coordinate = Math.min(p1.x, p2.x);
                    break;
                case "-X":
                    coordinate = Math.max(p1.x, p2.x);
                    break;
                case "+Y":
                    coordinate = Math.min(p1.y, p2.y);
                    break;
                case "-Y":
                    coordinate = Math.max(p1.y, p2.y);
                    break;
                case "+Z":
                    coordinate = Math.min(p1.z, p2.z);
                    break;
                case "-Z":
                    coordinate = Math.max(p1.z, p2.z);
                    break;
                default:
                    console.error("No direction matched.");
            }

            [this.force1, this.force2].forEach((force, i) => {
                if (force) {
                    const position = new THREE.Vector3( );
                    force.getWorldPosition(position);

                    position.setComponent(this.dirIndex, coordinate);

                    this.member.worldToLocal(position);

                    force.position.set(position.x, position.y, position.z);

                } else {
                    positionProjection = [p1, p2][i]; //omg genius
                    positionProjection.setComponent(this.dirIndex, coordinate);
                    this.member.worldToLocal(positionProjection);
                }
            });
        }


        // DRAW THE POLYGON
        [this.force1, this.force2].forEach((force, i) => {
            if (force) {

                const e = force.line.matrixWorld.elements; // World matrix
                const p03D = new THREE.Vector3( ); // point0 in 3D
                const p13D = new THREE.Vector3( );  // point1 in 3D
                const offset = new THREE.Vector3( );  // offset of point1 in 3D

                force.getWorldPosition(p03D);
                offset.set(e[4], e[5], e[6]);
                p13D.add(p03D).add(offset);

                // recalculate the position back to the member space
                this.member.worldToLocal(p03D);
                this.member.worldToLocal(p13D);

                let p02D = indices.map(index => p03D.toArray()[index]); // point 0 in 2D
                let p12D = indices.map(index => p13D.toArray()[index]);  // point 0 in 2D

                // console.log('member:', this.member.label.element.innerHTML)
                // console.log('p03D:', p03D);
                // console.log('p13D:', p13D);
                // console.log('p02D:', p02D);
                // console.log('p12D:', p12D);

                // It depends on the order of the points
                shapeCoords.push(i === 0 ? p02D : p12D);
                shapeCoords.push(i === 0 ? p12D : p02D);

            } else {
                // const p03D = this.member.localToWorld( new THREE.Vector3( this.xLoc[0], 0, 0 ) );
                let p02D;

                if (this.isProjection) {
                    p02D = [positionProjection.x, positionProjection.z]; // TODO: This will cause errors in the future
                } else {
                    p02D = [this.xLoc[i], 0]; // point 0 in 2D
                }

                shapeCoords.push( p02D );
            }
        });

        shapeCoords.push(shapeCoords[0]); // close the polygon

        try {
            const forcePolygon = turf.polygon( [ shapeCoords ] );
            const splitPolygons = turf.unkinkPolygon( forcePolygon );

            splitPolygons.features.forEach(feature => {
                const coordinates = feature.geometry.coordinates[0];

                const shapePoints = coordinates.map(point => new THREE.Vector2(point[0], point[1]));
                const shape = new THREE.Shape( shapePoints );
                const shapeGeometry = new THREE.ShapeGeometry( shape );
                const polygonMesh = new THREE.Mesh( shapeGeometry, this.meshMaterial );

                this.add( polygonMesh );
                this.objects.push( polygonMesh );

                polygonMesh.applyMatrix4(this.member.matrixWorld);

                if (this.dirIndex !== 1) {
                    polygonMesh.rotateX( Math.PI / 2 );
                }
            });
        } catch (error) {
            // console.log(shapeCoords);
            console.error('An error occured:', error.message);
        }

        const shapePoints = shapeCoords.map(point => new THREE.Vector2(point[0], point[1]));
        const lineGeo = new THREE.BufferGeometry().setFromPoints( shapePoints );
        const line = new THREE.Line( lineGeo, this.lineMaterial );
        this.add( line );
        this.objects.push( line );
        line.applyMatrix4(this.member.matrixWorld);

        if (this.dirIndex !== 1) {
            line.rotateX( Math.PI / 2 );
        }
    }

    setColor( color ) {
        if (this.force1) {
            this.force1.setColor( color );
        }

        if (this.force2) {
            this.force2.setColor( color );
        }

        this.meshMaterial.color.set(color);
        this.forceMeshMaterial.color.set(color);
        this.lineMaterial.color.set(color);
    }

    clear() {
        super.clear();
        if (this.force1) this.force1.clear();
        if (this.force2) this.force2.clear();
    }

}

export { DistributedLoadHelper };